import { cx } from 'emotion';
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { ListItem, theme } from '@ublend-npm/aulaui-next';
import Collapse from '@material-ui/core/Collapse';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { evaluateMaterialAction } from '@core/materials/permissions';

import ProgressBar from '../../../common/ProgressBar/ProgressBar';
import SectionListItem from './SectionListItem.container';
import FolderListItemMenu from './FolderListItemMenu.container';
import MaterialVisibilityToggle from './MaterialVisibilityToggle.container';
import AddSectionButton from './AddSectionButton.container';
import { listItem, hiddenListItem } from './MaterialListItem.styled';
import {
  ExpandedFolderIcon,
  CollapsedFolderIcon,
  SectionList,
  FolderButtons,
  folderItem,
  OverviewFolderContainer,
  OverviewFolderTitleContainer,
  OverviewFolderProgress,
} from './FolderListItem.styled';
import { useDrag, useDrop } from './MaterialListItem.dnd';

const CLASSES = {
  text: 'label',
};

const FolderListItem = (props) => {
  const {
    materialId,
    expanded,
    primaryText,
    hidden,
    toggleFolder,
    materialIds,
    isInstructor,
    isStudent,
    onReorder,
    educatorOnly,
    isOverview,
    allSectionsCompleted,
    sectionsProgress,
    canEdit,
    canCreate,
    canView = true,
    permissions = [],
    isRbacEnabled = false,
    hasProgressPermission = false,
    isReadOnlyUser = false,
    ...otherProps
  } = props;
  const listItemRef = useRef(null);
  const dragDropProps = { ...props, isFolder: true, parent: null, canEdit };
  const [{ isDragging }, drag] = useDrag(dragDropProps);
  const [{ dropType }, drop] = useDrop(dragDropProps, listItemRef);
  const ariaLabelId = `folder-button-${materialId}`;
  const { numberOfSections, numberOfSectionsCompleted } = sectionsProgress;

  const showGreenTick =
    hasProgressPermission && !isOverview && allSectionsCompleted;
  const showProgressbar = isOverview;
  const accessibilityLabel = showProgressbar
    ? `${primaryText}. With ${numberOfSectionsCompleted} out of ${numberOfSections} materials completed`
    : primaryText;

  // Check if AddSectionButton should be shown
  // For students: only show if RBAC is enabled and they have CREATE_PAGE permission
  // For instructors: always show (button will handle permission checking internally)
  const hasResolvedPermissions =
    Array.isArray(permissions) && permissions.length > 0;
  const fallbackAllowed = !isRbacEnabled
    ? canCreate
    : !hasResolvedPermissions && canCreate;
  const effectivePermissions = !isRbacEnabled ? [] : permissions;
  const createStatus = evaluateMaterialAction('CREATE_PAGE', {
    permissions: effectivePermissions,
    fallback: fallbackAllowed,
  });
  const shouldShowAddButton = isStudent
    ? isRbacEnabled && createStatus.allowed
    : true;

  drag(drop(listItemRef));

  // For students: check if they have toggle visibility permission to show eye icon
  const toggleVisibilityStatus =
    isStudent && isRbacEnabled
      ? evaluateMaterialAction('TOGGLE_VISIBILITY', {
          permissions,
          fallback: false,
        })
      : { allowed: false };

  const showEyeIconForStudent =
    isStudent && isRbacEnabled && toggleVisibilityStatus.allowed;

  const OverviewFolder = () => {
    const showStepsCounter =
      numberOfSections > 0 && numberOfSectionsCompleted !== undefined;

    return (
      <OverviewFolderContainer aria-label={accessibilityLabel}>
        <OverviewFolderTitleContainer>
          {primaryText}
          {showStepsCounter && (
            <OverviewFolderProgress data-testid="folder-steps-counter">
              {`${numberOfSectionsCompleted}/${numberOfSections}`}
            </OverviewFolderProgress>
          )}
        </OverviewFolderTitleContainer>
        <ProgressBar
          numberOfSections={numberOfSections}
          numberOfSectionsCompleted={numberOfSectionsCompleted}
        />
      </OverviewFolderContainer>
    );
  };

  return (
    <React.Fragment>
      <li
        style={{ padding: '8px 0px' }}
        ref={listItemRef}
        data-testid="materials-folder-list-item"
      >
        <ListItem
          component="div"
          textTruncate
          className={cx(listItem, folderItem, hidden && hiddenListItem)}
          classes={CLASSES}
          leftDecoration={
            expanded ? <ExpandedFolderIcon /> : <CollapsedFolderIcon />
          }
          accessibilityLabel={accessibilityLabel}
          rightDecoration={
            !isReadOnlyUser && (
              <>
                {canView && (
                  <FolderButtons id={ariaLabelId}>
                    <FolderListItemMenu
                      materialId={materialId}
                      ariaLabelId={ariaLabelId}
                      educatorOnly={educatorOnly}
                      isStudent={isStudent}
                    />
                  </FolderButtons>
                )}
                {isInstructor && !educatorOnly && canView && (
                  <MaterialVisibilityToggle isFolder materialId={materialId} />
                )}
                {showEyeIconForStudent && (
                  <MaterialVisibilityToggle isFolder materialId={materialId} />
                )}
                {showGreenTick && (
                  <CheckCircleOutlineIcon
                    style={{
                      color: `${theme.color.designSystem.textColors.green}`,
                      fontSize: `24px`,
                    }}
                    title="Progress Status"
                    aria-label="All materials in section have been marked as complete"
                    aria-hidden="false"
                    role="img"
                    data-testid={`folder-completed-icon-${materialId}`}
                  />
                )}
              </>
            )
          }
          onClick={toggleFolder}
          primaryText={!isOverview ? primaryText : undefined}
          isDraggable={canEdit}
          isDragging={canEdit ? isDragging : false}
          dropType={canEdit ? dropType : undefined}
          withTooltipInPosition="top"
          attributes={{
            'aria-expanded': expanded,
            'data-testid': 'material-folder',
          }}
          {...otherProps}
        >
          {showProgressbar ? <OverviewFolder /> : null}
        </ListItem>
      </li>
      <Collapse
        aria-hidden={!expanded}
        in={expanded}
        component="li"
        style={{ paddingBottom: expanded ? '8px' : '0' }}
        data-testid={`materials-folder-list-item-sections-${
          expanded ? 'expanded' : 'collapsed'
        }`}
      >
        <SectionList component="ul">
          {materialIds.map((id) => (
            <SectionListItem
              educatorOnly={educatorOnly}
              key={id}
              materialId={id}
            />
          ))}
          {shouldShowAddButton && !isReadOnlyUser && (
            <AddSectionButton
              folderId={materialId}
              educatorOnly={educatorOnly}
              canCreate={canCreate}
              permissions={permissions}
              isRbacEnabled={isRbacEnabled}
            />
          )}
        </SectionList>
      </Collapse>
    </React.Fragment>
  );
};

FolderListItem.propTypes = {
  materialId: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  hidden: PropTypes.bool.isRequired,
  materialIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  isInstructor: PropTypes.bool.isRequired,
  isStudent: PropTypes.bool.isRequired,
  toggleFolder: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  educatorOnly: PropTypes.bool,
  primaryText: PropTypes.string,
  isOverview: PropTypes.bool,
  allSectionsCompleted: PropTypes.bool.isRequired,
  sectionsProgress: PropTypes.shape({
    numberOfSections: PropTypes.number,
    numberOfSectionsCompleted: PropTypes.number,
  }),
  canEdit: PropTypes.bool,
  canCreate: PropTypes.bool,
  canView: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  hasProgressPermission: PropTypes.bool,
};

FolderListItem.defaultProps = {
  educatorOnly: false,
  isOverview: false,
  primaryText: '',
  sectionsProgress: {},
  canEdit: true,
  canCreate: true,
  canView: true,
  permissions: [],
  isRbacEnabled: false,
  hasProgressPermission: false,
};

export default FolderListItem;
